import { CheckIcon, CopyIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge, BadgeColorVariant, BadgeVariant } from "../ui/badge";

export function BadgeToClipboard({
  value,
  variant = BadgeVariant.Filled,
  colorVariant = BadgeColorVariant.Primary,
}: {
  value: string;
  variant?: BadgeVariant;
  colorVariant?: BadgeColorVariant;
}) {
  const { t } = useTranslation("common");
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setCopied(false);
        timeoutRef.current = null;
      }, 2000);
    } catch (_) {
      toast.error(t("ErrorCopyingToClipboard"));
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Badge
      variant={variant}
      colorVariant={colorVariant}
      onClick={handleCopy}
      tooltip={t("ClickToCopy", { value })}
      aria-label={t("ClickToCopy", { value })}
      style={{ flexBasis: "max-content" }}
    >
      <span className="mr-2 min-w-0 truncate">{value}</span>
      {copied ? <CheckIcon className="size-3 shrink-0" /> : <CopyIcon className="size-3 shrink-0" />}
    </Badge>
  );
}
